// The sample project (Stevenson's Jekyll and Hyde, SampleProject.swift) on the web: the bundled
// `Apps/Sample/Jekyll and Hyde.writingsuite`, read when the app is built into one hashed asset (scripts/build.mjs), put
// on the relay as a new project the way `wsc upload-sample --keep-id` does — but always under the package's own id
// (`SampleProject.projectID(version:)`, in the project document), never a copy's. So every device that adds it adds
// the same project: when the relay already has that id (another device put it there), this opens it and uploads
// nothing, and a Mac that has the sample syncs with it rather than holding a second one.
import * as A from '@automerge/automerge';
import { open as openDocs, type OpenedProject } from '@writing-suite/core';
import { ApiError, type Api } from '@writing-suite/client/net/api.ts';
import { newCid, SyncConnection } from '@writing-suite/client/net/sync.ts';
import { putProject } from '@writing-suite/client/net/vault.ts';
import { random, seal } from '@writing-suite/client/shared/crypto.ts';
import { fetchProjects } from './library.ts';

declare const __SAMPLE__: string | undefined;

/** Marks the note the sample opens on (`SampleProject.startField`). */
export const SAMPLE_START_FIELD = 'sample-start';

const MAGIC = 'WSSAMPLE1\n';

export interface PackageDoc {
  /** `project` or `fragment.<id>`, as the relay names documents. */
  name: string;
  bytes: Uint8Array;
}

/** The bundled package: "WSSAMPLE1\n", a JSON line naming each document and its length, then the documents' bytes. */
export function readSamplePackage(buf: Uint8Array): PackageDoc[] {
  const text = new TextDecoder();
  if (text.decode(buf.subarray(0, MAGIC.length)) !== MAGIC) throw new Error('The sample project in this build is damaged.');
  const lineEnd = buf.indexOf(10, MAGIC.length);
  const header = JSON.parse(text.decode(buf.subarray(MAGIC.length, lineEnd))) as { docs: { name: string; length: number }[] };
  let at = lineEnd + 1;
  return header.docs.map((d) => {
    const bytes = buf.slice(at, at + d.length);
    at += d.length;
    return { name: d.name, bytes };
  });
}

/** The same, written (the build's side; also the tests'). */
export function writeSamplePackage(docs: PackageDoc[]): Uint8Array {
  const head = new TextEncoder().encode(`${MAGIC}${JSON.stringify({ docs: docs.map((d) => ({ name: d.name, length: d.bytes.length })) })}\n`);
  const out = new Uint8Array(head.length + docs.reduce((n, d) => n + d.bytes.length, 0));
  out.set(head);
  let at = head.length;
  for (const d of docs) {
    out.set(d.bytes, at);
    at += d.bytes.length;
  }
  return out;
}

export interface SampleInfo {
  /** The project's own id (the package's), which the relay keeps. */
  projectId: string;
  title: string;
  type: string;
  words: number;
  /** The Start Here note: its node and arrangement (null: the manuscript's). */
  /** The Start Here note: its node, its arrangement (null: the manuscript), and whether that's a reference arrangement (Research). */
  start: { nodeId: string; arrangementId: string | null; research: boolean } | null;
}

const scalarString = (v: unknown) => (v instanceof A.ImmutableString ? v.val : typeof v === 'string' ? v : '');

/** What the package holds: its id and title (scalar strings, as the Swift writes them), its words, and Start Here. */
export function describeSample(docs: PackageDoc[]): SampleInfo {
  const loaded = new Map(docs.map((d) => [d.name, A.load(d.bytes) as A.Doc<unknown>]));
  const doc = loaded.get('project') as A.Doc<{ id?: unknown; title?: unknown }> | undefined;
  if (!doc) throw new Error('The sample project in this build has no project document.');
  const projectId = scalarString(doc.id);
  if (!/^[A-Za-z0-9_.:-]{1,128}$/.test(projectId)) throw new Error('The sample project in this build has no usable id.');
  const project = openDocs(loaded) as OpenedProject;
  return { projectId, title: scalarString(doc.title) || project.title, type: project.type, words: project.manuscriptWords(), start: startHere(project) };
}

/** The node whose document is marked `sample-start`, in whichever arrangement holds it. */
export function startHere(project: OpenedProject): SampleInfo['start'] {
  const manuscript = project.manuscriptId();
  for (const a of project.arrangements()) {
    if (a.role === 'trash') continue;
    const stack = [...project.tree(a.id)];
    while (stack.length) {
      const n = stack.shift()!;
      if (n.fragmentId && project.fragment(n.fragmentId)?.metadata[SAMPLE_START_FIELD]) return { nodeId: n.id, arrangementId: a.id === manuscript ? null : a.id, research: a.role === 'reference' };
      stack.unshift(...n.children);
    }
  }
  return null;
}

/** The bundled package, fetched (null when this build has none). */
export async function fetchSamplePackage(fetcher: typeof fetch = fetch): Promise<PackageDoc[] | null> {
  const url = typeof __SAMPLE__ === 'string' ? __SAMPLE__ : null;
  if (!url) return null;
  const res = await fetcher(url);
  if (!res.ok) throw new Error(`The sample project didn’t download (${res.status}).`);
  return readSamplePackage(new Uint8Array(await res.arrayBuffer()));
}

export interface SampleDeps {
  api: Api;
  accountId: string;
  avk: Uint8Array;
  /** The account's projects on the relay now (default: asks the relay). */
  list?: () => Promise<{ id: string }[]>;
  /** Uploads one project's documents (default: a SyncConnection's snapshots, as `wsc upload-sample`). */
  upload?: (projectId: string, key: Uint8Array, docs: PackageDoc[]) => Promise<void>;
  put?: typeof putProject;
}

/** Puts each document on the relay as its first snapshot, sealed with the project's key. */
async function uploadSnapshots(api: Api, projectId: string, key: Uint8Array, docs: PackageDoc[]) {
  const conn = new SyncConnection(api, projectId);
  await conn.connect();
  try {
    const { limits, head } = await conn.hello(0);
    if (head > 0) return; // another device got there first: its copy stands
    for (const d of docs) {
      const heads = A.getHeads(A.load(d.bytes)).sort().join('');
      const r = await conn.append('snapshot', { doc: d.name, epoch: 0, lineage: '', cid: newCid(), heads, base: 0 }, await seal(key, d.bytes), limits);
      if (r.t === 'error') throw new Error(`${d.name}: ${r.message}`);
    }
  } finally {
    conn.close();
  }
}

/**
 * Opens the sample project: on the relay already (under its own id) → nothing is sent; else it's added, sealed with a
 * new project key. Returns what to open and whether this browser added it.
 */
export async function openSample(docs: PackageDoc[], deps: SampleDeps): Promise<SampleInfo & { added: boolean }> {
  const info = describeSample(docs);
  const list = deps.list ?? (() => fetchProjects(deps.api));
  if ((await list()).some((p) => p.id === info.projectId)) return { ...info, added: false };
  const key = random(32);
  try {
    await (deps.put ?? putProject)(deps.api, deps.accountId, deps.avk, info.projectId, key, {
      v: 1,
      title: info.title,
      type: info.type,
      words: info.words,
      modifiedAt: new Date().toISOString(),
    }, { ifAbsent: true });
  } catch (e) {
    // Another device put it on the web since the list was read: its key and copy stand.
    if (e instanceof ApiError && e.status === 409 && e.code === 'exists') return { ...info, added: false };
    throw e;
  }
  await (deps.upload ?? ((id, k, d) => uploadSnapshots(deps.api, id, k, d)))(info.projectId, key, docs);
  return { ...info, added: true };
}
